/*
  I·DO Saft-Switcher
  Swaps the product page in place when another flavour is clicked
  (no full page load), keeps the chosen pack size, and syncs the pack
  quantity into the sticky add-to-cart form.
*/
if (!customElements.get('saft-switcher')) {
  const cache = new Map();
  let remembered = null; // pack quantity chosen by the customer, kept across swaps

  const pageUrlFor = (href) => {
    const next = new URL(window.location.href);
    next.pathname = new URL(href, window.location.origin).pathname;
    return next.toString();
  };

  const fetchUrlFor = (href, view) => {
    const url = new URL(href, window.location.origin);
    if (view) url.searchParams.set('view', view);
    return url.toString();
  };

  const load = (url) => {
    if (!cache.has(url)) {
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          cache.delete(url);
          throw error;
        });
      cache.set(url, request);
    }
    return cache.get(url);
  };

  // Scripts inserted with importNode never run: recreate them so inline
  // scripts and app blocks (Loox, Dealeasy…) initialise on the new product.
  // External files that are already loaded (theme assets) are not re-run.
  const runScripts = (root, loadedSrcs) => {
    root.querySelectorAll('script').forEach((oldScript) => {
      const type = oldScript.getAttribute('type');
      if (type && !['text/javascript', 'module', 'application/javascript'].includes(type)) return;
      if (oldScript.src && loadedSrcs.has(oldScript.src)) return;
      const script = document.createElement('script');
      [...oldScript.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
      script.dataset.saftClone = '';
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  };

  const swapTo = async (href, view, { push = true } = {}) => {
    const sections = [...document.querySelectorAll('[id^="shopify-section-template--"]')];
    sections.forEach((section) => section.classList.add('saft-is-loading'));

    try {
      const html = await load(fetchUrlFor(href, view));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scrollY = window.scrollY;
      const loadedSrcs = new Set([...document.scripts].map((script) => script.src).filter(Boolean));

      sections.forEach((section) => {
        const fresh = doc.getElementById(section.id);
        if (!fresh) {
          section.classList.remove('saft-is-loading');
          return;
        }
        const node = document.importNode(fresh, true);
        section.replaceWith(node);
        runScripts(node, loadedSrcs);
      });

      if (doc.title) document.title = doc.title;
      const canonical = doc.querySelector('link[rel="canonical"]');
      const currentCanonical = document.querySelector('link[rel="canonical"]');
      if (canonical && currentCanonical) currentCanonical.href = canonical.href;

      if (push) history.pushState({ saftSwitcher: true }, '', pageUrlFor(href));
      window.scrollTo({ top: scrollY });

      document.querySelector('saft-switcher .saft-card.is-current')?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('saft-switcher:changed', { detail: { url: href } }));
    } catch (error) {
      console.error('[saft-switcher]', error);
      window.location.href = pageUrlFor(href);
    }
  };

  window.addEventListener('popstate', (event) => {
    if (!event.state?.saftSwitcher) return;
    const switcher = document.querySelector('saft-switcher');
    if (!switcher) return window.location.reload();
    swapTo(window.location.pathname, switcher.dataset.view, { push: false });
  });

  customElements.define(
    'saft-switcher',
    class SaftSwitcher extends HTMLElement {
      connectedCallback() {
        if (!history.state?.saftSwitcher) history.replaceState({ saftSwitcher: true }, '', window.location.href);

        this.initPacks();

        // In the theme editor keep normal links so merchants can navigate.
        if (window.Shopify?.designMode) return;

        this.querySelectorAll('a[data-switch]').forEach((link) => {
          const prefetch = () => load(fetchUrlFor(link.href, this.dataset.view)).catch(() => {});
          link.addEventListener('pointerenter', prefetch, { once: true });
          link.addEventListener('touchstart', prefetch, { once: true, passive: true });
          link.addEventListener('focus', prefetch, { once: true });

          link.addEventListener('click', (event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            if (link.classList.contains('is-current')) return;

            this.querySelectorAll('.saft-card.is-current').forEach((card) => {
              card.classList.remove('is-current');
              card.removeAttribute('aria-current');
            });
            link.classList.add('is-current', 'is-loading');
            link.setAttribute('aria-current', 'true');
            swapTo(link.href, this.dataset.view);
          });
        });
      }

      initPacks() {
        this.packInputs = [...this.querySelectorAll('.saft-pack__input')];
        if (!this.packInputs.length) return;

        if (remembered) {
          const match = this.packInputs.find((input) => input.value === remembered);
          if (match) match.checked = true;
        }

        this.packInputs.forEach((input) =>
          input.addEventListener('change', () => {
            remembered = input.value;
            this.syncSticky();
          })
        );
        this.syncSticky();
      }

      // The sticky bar has its own form (`<form id>--alt`): mirror the pack quantity there.
      syncSticky() {
        const checked = this.packInputs.find((input) => input.checked);
        const stickyForm = document.getElementById(`${this.dataset.form}--alt`);
        if (!checked || !stickyForm) return;

        let hidden = stickyForm.querySelector('input[name="quantity"][data-saft]');
        if (!hidden) {
          if (stickyForm.querySelector('input[name="quantity"]')) return; // sticky has its own selector
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'quantity';
          hidden.dataset.saft = '';
          stickyForm.appendChild(hidden);
        }
        hidden.value = checked.value;
      }
    }
  );
}
